import React, { useState } from 'react';
import { registerUser } from '../../libs/api';
import styles from '@/components/Auth/Auth.module.less';
import Button from '../common/Button';
import ThreeBackground from './ThreeBackground';
import RegistrationSuccess from './RegistrationSuccess';
import GoogleSignIn from './GoogleSignIn';
import { Checkbox, Input, Form } from 'antd';
import { AES } from 'crypto-js';

const Register: React.FC = () => {
  const [form] = Form.useForm();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);

  const onFinish = async (values: {
    name: string;
    email: string;
    password: string;
    agreed: boolean;
  }) => {
    // 确保用户已同意隐私协议
    if (!values.agreed) {
      setError('请先阅读并同意隐私协议与服务条款');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      // 获取 AES 密钥
      const secretKey = process.env.REACT_APP_AES_SECRET_KEY;
      if (!secretKey) {
        console.error('AES_SECRET_KEY is not defined in environment variables.');
        setError('系统配置错误，请联系管理员。');
        setLoading(false);
        return;
      }

      // 使用 AES 加密密码
      const encryptedPassword = AES.encrypt(values.password, secretKey).toString();

      const result = await registerUser({
        name: values.name,
        email: values.email,
        password: encryptedPassword,
      });
      console.log('注册成功:', result);
      setLoading(false);
      // 显示注册成功页面，提示用户查收验证邮件
      setRegisteredEmail(values.email);
    } catch (error) {
      console.error('注册失败:', error);
      setError('注册失败，请检查您的信息。');
      setLoading(false);
    }
  };

  // 如果注册成功，显示成功页面
  if (registeredEmail) {
    return <RegistrationSuccess email={registeredEmail} />;
  }

  return (
    <div className={styles['auth-page']}>
      <ThreeBackground />
      <div className={styles['auth-container']}>
        <Form form={form} onFinish={onFinish} className={styles['auth-form']} layout="vertical">
          <h2>注册</h2>
          <Form.Item
            style={{ width: '100%' }}
            name="name"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 2, message: '用户名至少需要2个字符' },
              { max: 50, message: '用户名不能超过50个字符' },
            ]}
          >
            <Input placeholder="用户名" size="large" />
          </Form.Item>
          <Form.Item
            style={{ width: '100%' }}
            name="email"
            rules={[
              { required: true, message: '请输入电子邮件' },
              { type: 'email', message: '请输入有效的电子邮件地址' },
            ]}
          >
            <Input type="email" placeholder="电子邮件" size="large" />
          </Form.Item>
          <Form.Item
            style={{ width: '100%' }}
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少需要6个字符' },
              { max: 100, message: '密码不能超过100个字符' },
            ]}
          >
            <Input.Password placeholder="密码" size="large" />
          </Form.Item>
          {error && <div className={styles['error-message']}>{error}</div>}
          <Form.Item
            style={{ width: '100%' }}
            name="agreed"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(new Error('请先阅读并同意隐私协议与服务条款')),
              },
            ]}
          >
            <Checkbox>
              阅读并同意
              <a target="_blank" href="/privacy-policy">
                隐私声明
              </a>
              与
              <a target="_blank" href="/terms-of-service">
                服务条款
              </a>
            </Checkbox>
          </Form.Item>
          <Form.Item shouldUpdate style={{ width: '100%' }}>
            {() => {
              const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0);
              const allFieldsFilled =
                form.getFieldValue('name') &&
                form.getFieldValue('email') &&
                form.getFieldValue('password') &&
                form.getFieldValue('agreed');

              return (
                <Button
                  type="submit"
                  disabled={loading || !allFieldsFilled || hasErrors}
                  className="primaryButton"
                >
                  {loading ? '注册中...' : '注 册'}
                </Button>
              );
            }}
          </Form.Item>
          <div className={styles['auth-links']}>
            <a href="/login" className={styles['register-link']}>
              已有账号？立即登录
            </a>
          </div>
          {/* 第三方登录按钮 */}
          <div className={styles['oauth-buttons']}>
            <GoogleSignIn
              className={styles['google-signin']}
              onError={() => setError('Google登录失败，请重试')}
            />
          </div>
        </Form>
      </div>
    </div>
  );
};

export default Register;
